import { useMemo } from 'react';
import type { SoldLines } from '../types/agreements.types';
import { invoiceableLines, tableEmptyMessages } from '../utils/invoiceableLines';
import type { AmountBasis } from '../utils/amountBasis';
import { selectionKeySet } from '../utils/invoiceSelection';
import type { SelectionUnit } from '../utils/invoiceSelection';
import { linkedServiceNames } from '../utils/linkedServiceNames';
import { localDateKey } from '../utils/overdue';
import { addOnSelectionRow, serviceSelectionRow } from '../utils/selectionTableRows';
import type { AddOnSelectionRow, ServiceSelectionRow } from '../utils/selectionTableRows';

const EMPTY_LINES: SoldLines = { services: [], addOns: [] };

export interface SelectionTableRows {
    selectedKeys: ReadonlySet<string>;
    services: ServiceSelectionRow[];
    addOns: AddOnSelectionRow[];
    emptyMessages: { services: string; addOns: string };
}

export function useSelectionTableRows(
    lines: SoldLines | undefined,
    selection: SelectionUnit[],
    flexible: boolean,
    basis: AmountBasis = 'net',
): SelectionTableRows {
    const today = localDateKey(new Date());
    const selectedKeys = useMemo(() => selectionKeySet(selection), [selection]);
    const shown = useMemo(() => invoiceableLines(lines ?? EMPTY_LINES), [lines]);
    const serviceNames = useMemo(() => linkedServiceNames(lines?.services ?? []), [lines?.services]);
    const emptyMessages = useMemo(() => tableEmptyMessages(lines ?? EMPTY_LINES), [lines]);

    const services = useMemo(
        () => shown.services.map((service) => serviceSelectionRow(service, selectedKeys, flexible, today, basis)),
        [shown, selectedKeys, flexible, today, basis],
    );
    const addOns = useMemo(
        () => shown.addOns.map((addOn) => addOnSelectionRow(addOn, selectedKeys, serviceNames, today, basis)),
        [shown, selectedKeys, serviceNames, today, basis],
    );

    return { selectedKeys, services, addOns, emptyMessages };
}
